import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, ChevronRight, Heart, AlertCircle, HelpCircle, Zap, Target, Flame, CloudRain, Eye, Users, UserX, TrendingUp, Frown, Brain, BatteryLow, MessageCircle } from 'lucide-react';
import axios from 'axios';
import Disclaimer from '@/components/Disclaimer';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emotionIcons = {
  anxiety: AlertCircle,
  guilt: Heart,
  anger: Flame,
  sadness: CloudRain,
  shame: Eye,
  fear: AlertCircle,
  trauma: Brain,
  betrayal: UserX,
  abandonment: Users,
  loneliness: Users,
  jealousy: Eye,
  rejection: UserX,
  frustration: Zap,
  disappointment: Frown,
  overwhelm: BatteryLow,
  pressure: Target,
  confusion: HelpCircle,
  hopelessness: CloudRain
};

const categoryLabels = {
  core: 'Grundkänslor',
  trauma: 'Trauma & Sår',
  relationship: 'Relationer',
  growth: 'Personlig utveckling'
};

const categoryColors = {
  core: 'bg-primary/10',
  trauma: 'bg-rose-100/50',
  relationship: 'bg-violet-100/50',
  growth: 'bg-emerald-100/50'
};

export default function EmotionsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [emotions, setEmotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const c = {
    back: 'Tillbaka',
    allEmotions: 'Alla känslor',
    title: 'Känslor',
    understand: 'Förstå dina känslor',
    emotionsWithTools: 'känslor med igenkännande exempel och verktyg för självhjälp.',
    all: 'Alla',
    signs: 'Tecken att känna igen',
    examples: 'Så kan det låta',
    whatToDo: 'Vad du kan göra',
    affirmations: 'Affirmationer',
    reminder: 'Det är okej att känna det du känner. Du behöver inte ändra på något just nu.'
  };

  useEffect(() => {
    if (hasAccess) {
      fetchEmotions();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Känslor</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Känslor" />
      </div>
    );
  }

  const fetchEmotions = async () => {
    try {
      const response = await axios.get(`${API}/emotions`);
      setEmotions(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta känslor');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(emotions.map(e => e.category))];
  const filteredEmotions = selectedCategory 
    ? emotions.filter(e => e.category === selectedCategory)
    : emotions;

  return (
    <div className="min-h-screen bg-white" data-testid="emotions-page">
      {/* Header */}
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedEmotion ? setSelectedEmotion(null) : navigate('/dashboard', { state: { expandCategory: 'emotions' } })}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedEmotion ? c.allEmotions : c.back}
          </Button>
          <h1 className="font-heading text-xl font-semibold">{c.title}</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Emotion detail view */}
        {selectedEmotion ? (
          <div className="animate-fade-in space-y-8">
            {/* Header */}
            <div className={`${categoryColors[selectedEmotion.category]} rounded-2xl p-8 lg:p-12`}>
              <div className="flex items-center gap-4 mb-6">
                {(() => {
                  const Icon = emotionIcons[selectedEmotion.id] || Heart;
                  return (
                    <div className="w-14 h-14 rounded-full bg-card flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                  );
                })()}
                <span className="text-sm px-3 py-1 rounded-full bg-card/80">
                  {categoryLabels[selectedEmotion.category]}
                </span>
              </div>
              <h2 className="font-heading text-4xl font-light mb-4">{selectedEmotion.emotion}</h2>
              <p className="text-muted-foreground leading-relaxed text-lg">{selectedEmotion.description}</p>
            </div>

            {/* Signs */}
            <div className="bg-card rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Tecken att känna igen</h3>
              <ul className="space-y-3">
                {selectedEmotion.signs.map((sign, index) => (
                  <li key={index} className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                    <span className="text-muted-foreground">{sign}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Examples */}
            <div className="bg-secondary/30 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Så kan det låta</h3>
              <div className="space-y-3">
                {selectedEmotion.examples.map((example, index) => (
                  <p key={index} className="italic text-muted-foreground border-l-2 border-primary/30 pl-4">
                    {example}
                  </p>
                ))}
              </div>
            </div>

            {/* Self-help */}
            <div className="bg-card rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Vad du kan göra</h3>
              <div className="space-y-4">
                {selectedEmotion.self_help.map((help, index) => (
                  <div key={index} className="flex gap-4 items-start p-4 rounded-xl bg-secondary/30">
                    <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-foreground">{help}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Affirmations */}
            {selectedEmotion.affirmations && selectedEmotion.affirmations.length > 0 && (
              <div className="bg-primary/5 rounded-2xl p-8 space-y-4">
                <h3 className="font-heading text-2xl">Affirmationer</h3>
                <div className="space-y-3">
                  {selectedEmotion.affirmations.map((affirmation, index) => (
                    <p key={index} className="text-lg font-light text-center py-2 border-b border-border/30 last:border-0">
                      "{affirmation}"
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Gentle reminder */}
            <div className="text-center py-8">
              <p className="text-muted-foreground italic">
                "Det är okej att känna det du känner. Du behöver inte ändra på något just nu."
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="text-center mb-8 space-y-3 animate-fade-in">
              <h2 className="font-heading text-3xl font-light">Förstå dina känslor</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                {emotions.length} känslor med igenkännande exempel och verktyg för självhjälp.
              </p>
            </div>

            {/* Category filter */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                onClick={() => setSelectedCategory(null)}
                className="rounded-full"
                data-testid="filter-all"
              >
                Alla ({emotions.length})
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat)}
                  className="rounded-full"
                  data-testid={`filter-${cat}`}
                >
                  {categoryLabels[cat]} ({emotions.filter(e => e.category === cat).length})
                </Button>
              ))}
            </div>

            {/* Emotion list */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
              </div>
            ) : (
              <div className="space-y-3">
                {filteredEmotions.map((emotion, index) => {
                  const Icon = emotionIcons[emotion.id] || Heart;
                  return (
                    <button
                      key={emotion.id}
                      onClick={() => setSelectedEmotion(emotion)}
                      className={`w-full ${categoryColors[emotion.category]} hover:shadow-soft p-5 rounded-2xl text-left transition-shadow duration-300 opacity-0 animate-slide-up group flex items-center justify-between`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`emotion-card-${emotion.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-card flex items-center justify-center group-hover:-translate-y-1 transition-transform duration-300">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg font-normal">{emotion.emotion}</h3>
                          <p className="text-xs text-muted-foreground">{categoryLabels[emotion.category]}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform duration-300" />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Disclaimer */}
        <div className="mt-12">
          <Disclaimer variant="mini" />
        </div>
      </main>
    </div>
  );
}
